import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Quote: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [headerRef, headerVisible] = useScrollAnimation(0.3);
  const [formRef, formVisible] = useScrollAnimation(0.2);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const services = [
    'Brand Strategy & Positioning',
    'Creative Design & Development',
    'Digital Marketing Campaigns',
    'ATL Advertising',
    'BTL Activations',
    '3D Billboards & Displays',
    'Social Media Management',
    'Content Creation',
    'Event Management',
    'Other'
  ];

  const budgetRanges = [
    'Under 0.5 Million PKR',
    '0.5 - 1 Million PKR',
    '1 - 1.5 Million PKR',
    '1.5 - 2 Million PKR',
    '2 - 2.5 Million PKR',
    '2.5 - 3 Million PKR',
    '3 - 3.5 Million PKR',
    '3.5 - 4 Million PKR',
    '4 - 4.5 Million PKR',
    '4.5 - 5 Million PKR',
    'Over 5 Million PKR'
  ];

  const timelines = [
    'ASAP',
    'Within 1 month',
    '1-3 months',
    '3-6 months',
    '6+ months',
    'Flexible'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create WhatsApp message
    const whatsappMessage = `New Quote Request from Brand Aviation Website:

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Company:* ${formData.company || 'Not provided'}
*Service Needed:* ${formData.service}
*Budget Range:* ${formData.budget || 'Not specified'}
*Timeline:* ${formData.timeline || 'Not specified'}

*Project Details:*
${formData.message}

---
This message was sent from the Brand Aviation website quote form.`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/923145154682?text=${encodedMessage}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Show success message
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          budget: '',
          timeline: '',
          message: ''
        });
        setSubmitStatus('idle');
      }, 3000);
    }, 1000);
  };

  return (
    <section id="quote" className="relative py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-primary">
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Floating Elements - Responsive */}
        <div className="absolute top-10 sm:top-20 left-4 sm:left-10 w-8 h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-brand-orange/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-20 sm:top-40 right-4 sm:right-20 w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 bg-brand-gold/25 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute bottom-20 sm:bottom-40 left-1/4 w-10 h-10 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-brand-orange/15 rounded-full blur-2xl animate-ping"></div>
        <div className="absolute bottom-10 sm:bottom-20 right-1/3 w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-brand-gold/30 rounded-full blur-md animate-pulse"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
        {/* Enhanced Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Badge */}
          <div className={`inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6 transition-all duration-1000 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.1s' }}>
            <div className="w-2 h-2 bg-brand-orange rounded-full animate-pulse"></div>
            <span>Get Your Quote</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 md:mb-8 transition-all duration-700">
            <span className={`block transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.2s' }}>
              Ready to Soar?
            </span>
            <span className={`block bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.4s' }}>
              Get Your Quote
            </span>
          </h2>
          
          <div className={`w-16 sm:w-20 md:w-24 h-0.5 sm:h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-4 sm:mb-6 md:mb-8 transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.6s' }}></div>
          
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 max-w-3xl sm:max-w-4xl mx-auto leading-relaxed px-2 sm:px-0 transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.8s' }}>
            Let's discuss your project and create something extraordinary together. 
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>

        {/* Quote Form */}
        <div 
          ref={formRef}
          className={`max-w-xs sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto transition-all duration-1000 ${
            formVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative overflow-hidden">
            {/* Form Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-brand-gold/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
            
            {/* Main Form Card */}
            <div className="relative bg-white/95 backdrop-blur-sm border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12 shadow-2xl shadow-brand-orange/10">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center text-3xl text-white mx-auto mb-6 animate-bounce">
                    ✓
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    We've received your quote request and will get back to you within 24 hours.
                  </p>
                  <button 
                    onClick={() => setSubmitStatus('idle')}
                    className="bg-gradient-to-r from-brand-orange to-brand-gold text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 md:space-y-8">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="company" className="block text-sm font-semibold text-gray-700">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        placeholder="Enter your company name"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <label htmlFor="service" className="block text-sm font-semibold text-gray-700">
                        Service Needed *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="budget" className="block text-sm font-semibold text-gray-700">
                        Budget Range
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                      >
                        <option value="">Select budget range</option>
                        {budgetRanges.map((range) => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="timeline" className="block text-sm font-semibold text-gray-700">
                      Project Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((timeline) => (
                        <option key={timeline} value={timeline}>{timeline}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-3 py-2.5 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm resize-none text-sm sm:text-base"
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="text-center pt-2 sm:pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative overflow-hidden bg-gradient-to-r from-brand-orange via-brand-orange-light to-brand-gold text-white px-6 py-3 sm:px-8 sm:py-3.5 md:px-10 md:py-4 lg:px-12 lg:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base md:text-lg shadow-2xl shadow-brand-orange/25 transition-all duration-500 hover:shadow-3xl hover:shadow-brand-orange/40 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    >
                        <span className="relative z-10 flex items-center justify-center space-x-2 sm:space-x-3">
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <span>Get My Quote</span>
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </>
                        )}
                      </span>
                      
                      {/* Animated Background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-orange-dark via-brand-orange to-brand-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="text-center mt-8 sm:mt-12 md:mt-16">
          <p className="text-white/80 text-sm sm:text-base md:text-lg mb-3 sm:mb-4 px-2 sm:px-0">
            Prefer to talk directly? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 md:space-x-8">
            <a 
              href="tel:+923145154682" 
              className="flex items-center space-x-2 text-white hover:text-brand-orange transition-colors duration-300 text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>0314-5154682</span>
            </a>
            <a 
              href="mailto:info@brandaviation.com" 
              className="flex items-center space-x-2 text-white hover:text-brand-orange transition-colors duration-300 text-sm sm:text-base"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>info@brandaviation.com</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quote;
